/**
 * /legal/cookies — Cookie Policy.
 *
 * Source contract: docs/ux/journeys/J-X14-cookie-consent.md § Cookie classification.
 * Lists which cookies fall under "essential" (no consent required) and which
 * fall under "analytics" (opt-in via banner).
 *
 * Substrate: LegalPage wrapper from ./LegalPage.tsx.
 */

import type { ReactElement } from 'react';
import { COOKIES_VERSION } from '@bimassess/db/legal-versions';
import { LegalPage } from './LegalPage.tsx';

export function CookiePolicyPage(): ReactElement {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Cookie Policy"
      version={COOKIES_VERSION}
    >
      <p>
        This page explains the cookies BIMAssess sets, why we set them, and
        how you can control them. We classify cookies into two categories.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        Essential cookies (no consent required)
      </h2>
      <p>
        These cookies are strictly necessary to deliver the service you
        explicitly requested — keeping you signed in, protecting against
        cross-site request forgery, and (in embedded-iframe flows) carrying
        partitioned session state. They cannot be turned off through the
        consent banner.
      </p>
      <ul className="list-disc space-y-1 pl-6">
        <li>
          <strong>Session cookie</strong> — issued by Better-Auth to keep you
          signed in across requests.
        </li>
        <li>
          <strong>CSRF protection</strong> — short-lived token used to verify
          form submissions.
        </li>
        <li>
          <strong>Embed-mode partitioned cookie</strong> — when an assessment
          runs inside a host iframe, a{' '}
          <code>SameSite=None; Secure; Partitioned</code> cookie carries
          per-host session state under CHIPS.
        </li>
      </ul>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        Analytics cookies (opt-in)
      </h2>
      <p>
        These cookies help us understand how the product is used so we can
        improve it. They are off by default and only load if you have opted
        in via the cookie consent banner or your Cookie preferences.
      </p>
      <ul className="list-disc space-y-1 pl-6">
        <li>
          <strong>Sentry session replay</strong> — captures anonymised
          interaction streams to diagnose front-end errors.
        </li>
        <li>
          <strong>Performance metrics</strong> — aggregate timing of page
          loads and user actions; never tied to identity beyond the session.
        </li>
      </ul>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        Managing your choices
      </h2>
      <p>
        On your first visit you will see a consent banner with three options:
        <strong> Accept all</strong>, <strong>Essential only</strong>, or
        <strong> Customise</strong>. Your choice is stored in a first-party
        cookie called <code>cookie_consent</code> for 12 months.
      </p>
      <p>
        You can change your mind at any time by clicking{' '}
        <strong>Cookie preferences</strong> in the footer of any page.
      </p>

      <h2 className="mt-8 font-sans text-h3 font-medium tracking-tight">
        Withdrawal of consent
      </h2>
      <p>
        When you switch from "Accept all" to "Essential only" we stop
        loading analytics scripts on your next page load, and any
        previously-set analytics cookies are cleared on your next response
        from the server.
      </p>
    </LegalPage>
  );
}
